import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Time-Critical & AOG', path: '/time-critical' },
    { name: 'Air Freight', path: '/air-freight' },
    { name: 'Road Freight', path: '/road-freight' },
    { name: 'Sea Freight', path: '/sea-freight' },
    { name: 'Onboard Courier', path: '/onboard-courier' },
    { name: 'Aircraft Charter', path: '/aircraft-charter' },
    { name: 'Dangerous Goods', path: '/dangerous-goods' },
    { name: 'Customs Clearance', path: '/customs' },
    { name: 'ATC Insights', path: '/insights' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
        data-testid="main-navigation"
      >
        {/* Top Bar */}
        <div className="bg-[#003057]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-11 text-sm text-white">
              <div className="flex items-center gap-6">
                <a href="tel:+441753720055" className="flex items-center gap-2 hover:text-[#00A3E0] transition-colors">
                  <Phone size={14} />
                  <span>+44 (0) 1753 720055</span>
                </a>
                <a href="mailto:booking@aerotimecritical.co.uk" className="hidden md:flex items-center gap-2 hover:text-[#00A3E0] transition-colors">
                  <Mail size={14} />
                  <span>booking@aerotimecritical.co.uk</span>
                </a>
              </div>
              <div className="text-[#C9A227] font-semibold tracking-wide">24/7/365 SUPPORT</div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 md:h-32">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <motion.img
                whileHover={{ scale: 1.03 }}
                src="/atc-logo.png"
                alt="Aero Time Critical - global time-critical freight forwarding"
                className="h-16 md:h-24 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.slice(0, 4).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-semibold transition-colors ${
                    location.pathname === link.path ? 'text-[#0089D0]' : 'text-[#003057] hover:text-[#0089D0]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="relative group">
                <button className="text-sm font-semibold text-[#003057] hover:text-[#0089D0] transition-colors">
                  Services
                </button>
                <div className="absolute top-full left-0 mt-3 w-56 bg-white border border-[#E5E9EF] shadow-xl rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  {navLinks.slice(4, 9).map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-3 text-sm text-[#003057] hover:bg-[#F7F9FB] hover:text-[#0089D0] transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
              {navLinks.slice(9).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-semibold transition-colors ${
                    location.pathname === link.path ? 'text-[#0089D0]' : 'text-[#003057] hover:text-[#0089D0]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/quote">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                  data-testid="nav-get-quote-btn"
                >
                  Get a Quote
                </motion.button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-[#003057] hover:text-[#0089D0] transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed inset-0 z-40 bg-white lg:hidden overflow-y-auto pt-44"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-4 text-lg font-semibold border-b border-[#E5E9EF] ${
                      location.pathname === link.path ? 'text-[#0089D0]' : 'text-[#003057]'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <Link to="/quote" onClick={() => setIsOpen(false)}>
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="btn-primary w-full mt-8"
                >
                  Get a Quote
                </motion.button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
